import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../config';
import { Product } from '../types/products';
import { useTheme } from './ThemeContext';
import { IconCheck, IconX } from './index';
import PathwayTimeline from './chronology/PathwayTimeline';
import { buildChronologyModel } from './chronology/chronologyAdapters';
import { ChronologyKeyframes } from './chronology/chronologyTokens';

// ============================================================
// HELPER: case-insensitive field lookup on a loosely-shaped row
// ============================================================
export function getField(row: Record<string, unknown>, ...candidates: string[]): unknown {
  // Build a case-insensitive lookup map once per row
  const lowerMap: Record<string, string> = {};
  for (const key of Object.keys(row)) {
    lowerMap[key.toLowerCase()] = key;
  }
  for (const c of candidates) {
    // Try exact match first, then case-insensitive
    if (row[c] !== undefined && row[c] !== null) return row[c];
    const actual = lowerMap[c.toLowerCase()];
    if (actual && row[actual] !== undefined && row[actual] !== null) return row[actual];
  }
  return null;
}

// ============================================================
// HELPER: useMediaQuery hook for responsive design
// ============================================================
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

// ============================================================
// COMPONENT: ProductIntelBlock
//
// Reusable product intelligence block: given any row that carries a product
// `id` (e.g. a NL-SQL result row), it fetches the authoritative product
// record and renders the Supply Chain Pathway (same chronology components as
// the product detail page), the compliance grid and the key facts. Data
// changes on the product side propagate here with zero duplication.
// ============================================================
export default function ProductIntelBlock({
  row,
  onNavigate,
}: {
  row: Record<string, unknown>;
  onNavigate?: (id: number) => void;
}) {
  const { T } = useTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isTablet = useMediaQuery('(max-width: 1024px)');

  const productId = getField(row, 'id', 'product_id') as number | null;

  // The SQL row only carries whatever columns the LLM happened to SELECT.
  // Compliance and the supply-chain pathway are fetched from the
  // deterministic product-detail endpoint instead of being derived from it.
  const [product, setProduct] = useState<Product | null>(null);
  const [loadingProduct, setLoadingProduct] = useState<boolean>(productId != null);

  useEffect(() => {
    if (productId == null) return;
    let cancelled = false;
    setLoadingProduct(true);
    fetch(`${API_BASE}/metasupply/products/${productId}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (!cancelled) setProduct(data); })
      .catch(() => { if (!cancelled) setProduct(null); })
      .finally(() => { if (!cancelled) setLoadingProduct(false); });
    return () => { cancelled = true; };
  }, [productId]);

  // Product fields (authoritative) override the raw SQL row.
  const merged: Record<string, unknown> = product ? { ...row, ...product } : row;

  const productName = getField(merged, 'product_name', 'proprietary_name', 'name') as string | null;
  const riskLevel = getField(merged, 'supply_chain_risk', 'risk', 'risk_level') as string | null;
  const country = getField(merged, 'country_of_origin', 'api_source_country', 'finished_goods_country') as string | null;
  const manufacturer = getField(merged, 'manufacturer', 'manufacturer_name') as string | null;
  const dosageForm = getField(merged, 'dosage_form') as string | null;
  const strength = getField(merged, 'strength') as string | null;
  const therapeuticClass = getField(merged, 'therapeutic_class', 'pharm_class', 'pharmacological_class') as string | null;
  const productType = getField(merged, 'product_type') as string | null;
  const ndc = getField(merged, 'ndc_code', 'ndc') as string | null;

  // Compliance grid — only from the authoritative product record. Deriving
  // pass/fail from an arbitrary SQL row rendered "unknown" as a red X.
  const complianceItems: Array<{ label: string; pass: boolean }> = [];
  if (product) {
    const shortageStatus = (product.shortage_status || '').toLowerCase();
    const hasActiveShortage = !!shortageStatus && shortageStatus !== 'none' && shortageStatus !== 'resolved';
    const cooVerified = !!product.country_of_origin && product.country_of_origin !== 'Unknown';
    complianceItems.push(
      { label: 'BAA', pass: !!product.baa_eligible },
      { label: 'TAA', pass: !!product.taa_eligible },
      { label: 'COO', pass: cooVerified },
      { label: 'Recalls', pass: (product.recalls?.length ?? 0) === 0 },
      { label: 'Shortages', pass: !hasActiveShortage },
    );
  }

  // Supply Chain Pathway (chronology model) — same rendering as the product page.
  const chronoModel = product && product.supply_chain ? buildChronologyModel(product) : null;

  // Key facts
  const facts: Array<{ label: string; value: string }> = [];
  if (manufacturer) facts.push({ label: 'Manufacturer', value: manufacturer });
  if (riskLevel) facts.push({ label: 'Supply Chain Risk', value: riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1) });
  if (country) facts.push({ label: 'Country of Origin', value: country });
  if (product?.coo_scoring_tier) facts.push({ label: 'COO Tier', value: product.coo_scoring_tier });
  // MIA Score intentionally not shown: made_in_america_score only reflects the
  // COO half today (API-source feeds not wired yet) — misleading next to a
  // foreign COO. Re-add once the score is complete.
  // Confidence intentionally not shown: products.confidence_score is the 0.80
  // column default on every row (no agent writes it yet).
  if (therapeuticClass) facts.push({ label: 'Therapeutic Class', value: therapeuticClass });
  if (dosageForm) facts.push({ label: 'Dosage Form', value: `${dosageForm}${strength ? ` ${strength}` : ''}` });
  if (productType) facts.push({ label: 'Product Type', value: productType });
  if (ndc) facts.push({ label: 'NDC', value: ndc });

  const canNavigate = productId != null && onNavigate != null;

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        borderTop: `2px solid ${T.gold}`,
        background: T.offWhite,
        padding: isMobile ? 16 : 24,
      }}
    >
      {/* Loading state while the full product record is fetched */}
      {loadingProduct && (
        <div style={{
          padding: '14px 0', marginBottom: 16, textAlign: 'center',
          fontSize: 12, fontWeight: 600, color: T.textLight,
          background: T.white, borderRadius: T.radiusSm, border: `1px solid ${T.border}`,
        }}>
          Loading full intelligence profile…
        </div>
      )}

      {/* Supply Chain Pathway — same component as the product detail page */}
      {chronoModel && (
        <div style={{ marginBottom: 24 }}>
          <ChronologyKeyframes />
          <PathwayTimeline model={chronoModel} />
        </div>
      )}

      {/* Compliance Status Grid — only from the authoritative product record */}
      {complianceItems.length > 0 && (
      <div style={{ marginBottom: 24 }}>
        <div style={{
          fontSize: 10, fontWeight: 700, color: T.textLight,
          textTransform: 'uppercase', letterSpacing: '0.08em',
          marginBottom: 10,
        }}>
          Compliance Status
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : isTablet ? 'repeat(3, 1fr)' : 'repeat(5, 1fr)',
          gap: 0,
          background: T.white,
          border: `1px solid ${T.border}`,
          borderRadius: T.radiusSm,
          overflow: 'hidden',
        }}>
          {complianceItems.map((item, i) => {
            const isLastInRow = isMobile ? (i % 2 === 1) : isTablet ? (i % 3 === 2) : (i === complianceItems.length - 1);
            return (
              <div
                key={item.label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  padding: isMobile ? '10px 8px' : '12px 10px',
                  borderRight: isLastInRow ? 'none' : `1px solid ${T.border}`,
                  borderBottom: (isMobile && i < complianceItems.length - 1) || (isTablet && i < 3) ? `1px solid ${T.border}` : 'none',
                }}
              >
                <span style={{
                  width: 18, height: 18, borderRadius: '50%',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  background: item.pass ? T.successBg : T.dangerBg,
                  color: item.pass ? T.success : T.danger,
                  flexShrink: 0,
                }}>
                  {item.pass ? <IconCheck /> : <IconX />}
                </span>
                <span style={{
                  fontSize: 11, fontWeight: 700,
                  color: item.pass ? T.success : T.danger,
                }}>
                  {item.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* Key Supply Chain Facts */}
      {facts.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{
            fontSize: 10, fontWeight: 700, color: T.textLight,
            textTransform: 'uppercase', letterSpacing: '0.08em',
            marginBottom: 10,
          }}>
            Key Supply Chain Facts
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: 8,
          }}>
            {facts.map((fact) => (
              <div
                key={fact.label}
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 8,
                  padding: '8px 12px',
                  background: T.white,
                  borderRadius: 6,
                  border: `1px solid ${T.borderLight}`,
                }}
              >
                <span style={{
                  fontSize: 10, fontWeight: 700, color: T.textLight,
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                  whiteSpace: 'nowrap', flexShrink: 0,
                }}>
                  {fact.label}
                </span>
                <span style={{
                  fontSize: 13, fontWeight: 600, color: T.textDark,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {fact.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* View Full Profile Link */}
      {canNavigate && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(productId);
          }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '10px 24px', borderRadius: T.radiusSm,
            border: `2px solid ${T.navy}`, background: T.navy,
            color: T.white, fontSize: 13, fontWeight: 700,
            cursor: 'pointer', transition: 'all 0.2s',
            fontFamily: 'inherit',
          }}
        >
          View Full Profile for {productName || 'Product'}
          <span style={{ fontSize: 14 }}>{"→"}</span>
        </button>
      )}
    </div>
  );
}
